import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { newAuth } from '@/firebase/firebaseInit';
import {
  RFP_API_SERVER_URL,
  RP_INTEGRATION_SERVER_URL,
  PARTNER_PERFORMANCE_GUARDIAN_API_SERVER_URL,
  PARTNER_ANALYTICS_SERVER_URL,
  ALERT_API_SERVER_URL,
} from '@/constants/constants';
import { storeRedirectAfterLogin } from '@/utils/authRedirect';
import { isAuthFailureResponse } from '@/utils/apiAuthFailure';
import { isRateLimitedBody, notifyUsageLimit } from '@/utils/usageLimitNotice';

// Resolves once Firebase Auth has determined the initial auth state (may be null for signed-out users).
// Avoids race conditions where onMounted fires before currentUser is populated from localStorage.
const authStateReady: Promise<void> = new Promise((resolve) => {
  const unsubscribe = onAuthStateChanged(newAuth, () => {
    unsubscribe();
    resolve();
  });
});

// --- Types ---

/**
 * No `apiKey` field by design. Vite inlines every `import.meta.env` value into the JS bundle,
 * so a service key configured here is published to every visitor rather than kept secret —
 * and the rp-users key previously sent this way was accepted by that service *in place of* a
 * user session. Backends must authenticate the per-user Firebase token instead.
 */
export interface ApiClientConfig {
  baseURL: string;
  timeout?: number;
  customHeaders?: Record<string, string>;
  /** Use uid header instead of Bearer token. No 401 retry logic. */
  authStrategy?: 'bearer' | 'uid-only';
}

// Extends internal config to track if we've already tried to refresh the token
interface AxiosRequestConfigWithRetry extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Interface for queued requests waiting for a fresh token
interface QueueItem {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}

// --- Token Refresh Management ---

// Flags to prevent multiple simultaneous token refresh calls
let isRefreshing = false;
let failedQueue: QueueItem[] = [];

// Helper to process all queued requests once a new token is obtained
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });

  failedQueue = [];
};

// --- Factory Function ---

/**
 * Creates a configured Axios instance with automatic token management.
 *
 * Strategy:
 * 1. Optimistic: Use cached token first (getIdToken(false)) for speed.
 * 2. Reactive: If 401 occurs, pause requests, refresh token, and retry.
 * 3. Concurrent: If multiple requests fail, only refresh once and queue the others.
 */
const createApiClient = (config: ApiClientConfig): AxiosInstance => {
  const instance = axios.create({
    baseURL: config.baseURL,
    timeout: config.timeout,
    headers: {
      'Content-Type': 'application/json',
      ...config.customHeaders,
    },
  });

  // --- Request Interceptor ---
  // Automatically attaches the per-user auth token to every outgoing request
  instance.interceptors.request.use(
    async (requestConfig: InternalAxiosRequestConfig) => {
      await authStateReady;

      const user = newAuth.currentUser;
      const useUidOnly = config.authStrategy === 'uid-only';

      if (user) {
        if (useUidOnly) {
          // uid-only: no Bearer token, only uid header
          if (!requestConfig.headers.uid) {
            requestConfig.headers.uid = user.uid;
          }
        } else {
          try {
            const token = await user.getIdToken(false);
            if (token) {
              requestConfig.headers.Authorization = `Bearer ${token}`;
              if (!requestConfig.headers.uid) {
                requestConfig.headers.uid = user.uid;
              }
            }
          } catch (error) {
            console.error('[API] Error attaching token:', error);
          }
        }
      }

      return requestConfig;
    },
    (error) => Promise.reject(error),
  );

  // --- Response Interceptor ---
  // Handles errors globally. Specifically looks for 401s to trigger token refresh.
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfigWithRetry;
      const useUidOnly = config.authStrategy === 'uid-only';

      // An enforced usage limit (HTTP 429, ticket 2026-10-18-usage-limits-and-traffic): one
      // notice per limit and retry period; the error still reaches the caller.
      if (error.response?.status === 429 && isRateLimitedBody(error.response.data)) {
        notifyUsageLimit(error.response.data);
        return Promise.reject(error);
      }

      // If the response means "not authenticated" and we haven't already retried this
      // request. Covers a 401 and rp-integrations' 400 ERR_MISSING_USER_ID reply — see
      // isAuthFailureResponse. Skip retry for uid-only clients (token refresh doesn't apply)
      if (
        !useUidOnly &&
        isAuthFailureResponse(error) &&
        originalRequest &&
        !originalRequest._retry
      ) {
        // Mark as retried to avoid infinite loops
        originalRequest._retry = true;

        // If another request is already refreshing the token, wait for it
        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              // Once token is refreshed, update header and retry
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        // Start the refresh process
        isRefreshing = true;

        try {
          const user = newAuth.currentUser;
          if (!user) {
            // No Firebase user at all means the session is gone, not a transient
            // network problem. Tag it so the isAuthError check below forces the
            // logout+redirect instead of leaving a dead session in place.
            const missingUserError = new Error('User not authenticated during refresh') as Error & {
              code?: string;
            };
            missingUserError.code = 'auth/user-not-found';
            throw missingUserError;
          }

          // Force a fresh token from Firebase server
          const newToken = await user.getIdToken(true);

          // Retry all queued requests with the new token
          processQueue(null, newToken);

          // Retry the original failed request
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }

          return instance(originalRequest);
        } catch (refreshError: any) {
          // If refresh fails, fail all queued requests
          processQueue(refreshError, null);

          // Only force logout if the refresh error indicates an invalid/revoked session
          // (e.g. user deleted, expired token, revoked token) and not on network/temporary errors.
          const isAuthError =
            refreshError &&
            (refreshError.code === 'auth/user-token-expired' ||
              refreshError.code === 'auth/user-not-found' ||
              refreshError.code === 'auth/invalid-user-token' ||
              refreshError.code === 'auth/user-disabled' ||
              refreshError.code === 'auth/invalid-credential' ||
              (refreshError.message &&
                (refreshError.message.includes('expired') ||
                  refreshError.message.includes('revoked') ||
                  refreshError.message.includes('invalid') ||
                  refreshError.message.includes('not found'))));

          if (isAuthError) {
            console.warn(
              '[API] Token refresh failed with auth error. Forcing logout...',
              refreshError,
            );
            try {
              await signOut(newAuth);
            } catch (logoutError) {
              console.error('[API] Error signing out during refresh failure:', logoutError);
            }
            localStorage.removeItem('userAddData');

            const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            storeRedirectAfterLogin(currentPath);
            const savedLanguage = localStorage.getItem('language');
            const langQuery = savedLanguage ? `&lang=${savedLanguage}` : '';
            window.location.href = `/auth/login?revoked=true${langQuery}&redirect=${encodeURIComponent(currentPath)}`;
          } else {
            console.error(
              '[API] Token refresh failed with non-auth error (e.g. network issue). Keeping session active.',
              refreshError,
            );
          }

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Return any other error as is
      return Promise.reject(error);
    },
  );

  return instance;
};

// --- Pre-configured Clients ---

const rfpApiClient = createApiClient({
  baseURL: RFP_API_SERVER_URL,
});

// These backend proxies default to PRODUCTION when `?environment=` is absent.
// Default them to the matching backend environment so back-office pages don't
// silently read prod. Development portals use development backend data: the dev
// rp-users can only read secrets of its own GCP project, so asking it for staging
// fails with PERMISSION_DENIED.
// Spreading the caller's params last keeps explicit callers (for example the
// fleet onboarding wizard) in control.
rfpApiClient.interceptors.request.use((requestConfig: InternalAxiosRequestConfig) => {
  // Explicit override (VITE_BACKEND_DATA_ENVIRONMENT=production|staging) lets a local/dev portal
  // read a specific backend without flipping VITE_ENVIRONMENT (which also gates other behaviour).
  const dataEnvOverride = import.meta.env.VITE_BACKEND_DATA_ENVIRONMENT;
  const portalEnvironment =
    dataEnvOverride === 'production' || dataEnvOverride === 'staging'
      ? dataEnvOverride
      : import.meta.env.VITE_ENVIRONMENT === 'production'
        ? 'production'
        : import.meta.env.VITE_ENVIRONMENT === 'development'
          ? 'development'
          : 'staging';
  const url = requestConfig.url ?? '';
  const shouldDefaultEnvironment =
    url.includes('/backend-user-service-proxy/') || url.includes('/backend-legacy-proxy/');
  if (shouldDefaultEnvironment) {
    requestConfig.params = { environment: portalEnvironment, ...requestConfig.params };
  }
  return requestConfig;
});

const rpIntegrationClient = createApiClient({
  baseURL: RP_INTEGRATION_SERVER_URL,
});

// DEPRECATED BACKENDS — do not add callers.
// partner-analytics and partner-performance-guardian authenticate only by shared API key; they
// do not verify Firebase tokens at all. Since that key can only reach the browser by being
// published in the bundle, it has been removed, and these two clients can no longer authenticate.
// Pages still calling them are listed in the security report and need retiring or repointing at
// rp-integrations.
const partnerPerformanceGuardianClient = createApiClient({
  baseURL: PARTNER_PERFORMANCE_GUARDIAN_API_SERVER_URL,
  authStrategy: 'uid-only',
});

const partnerAnalyticsClient = createApiClient({
  baseURL: PARTNER_ANALYTICS_SERVER_URL,
});

const alertClient = createApiClient({
  baseURL: ALERT_API_SERVER_URL,
  timeout: 10000,
});

export const apiClient = {
  rfp: rfpApiClient,
  rpIntegration: rpIntegrationClient,
  partnerPerformanceGuardian: partnerPerformanceGuardianClient,
  partnerAnalytics: partnerAnalyticsClient,
  alert: alertClient,
};
